import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FaExclamationCircle } from 'react-icons/fa';

import { UseOutletContextPos } from '../Pos';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { TreasuryAccountDA } from '../../../../services/admin/treasury.account.service';
import { StorageCenterDA } from '../../../../services/admin/storage.center.service';
import { ApiDigitalAdminContact } from '../../../../services/admin/contact.service';
import { Contact } from '../../../../models/contact.model';
import { TreasuryAccount } from '../../../../models/treasury.account.model';
import { evaluateLegendRequiredIcon } from '../../../../scripts/validate.legend.script';
import { legendValid, legendInvalid } from '../../../../utils/legend.data.util';
import { expressions } from '../../../../libraries/regular.expressions.library';
import { StorageCenter } from '../../../../models/storage.center.model';
import { prefixLegend } from '../../../../scripts/name.legend.script';
import { formatContact } from '../../../../scripts/format.value.script';
import { uploadTooltip } from '../../../../utils/tooltip.util';

import AppLegend from '../../../../components/element/Legend';
import AppInputTreasuryAccount from '../../../../components/element/InputTreasuryAccount';

export interface AppPosCashierFormProps {
  component?: string,
  name: {value: string, valid: boolean},
  code: {value: string, valid: boolean},
  stockEnabled: {value: boolean, valid: boolean},
  storageCenter: {value: string, valid: boolean, object: any},
  treasuryCashAccounts: {value: string[], valid: boolean},
  treasuryCard: {value: string, valid: boolean, object: any},
  defaultCashCurrency: {value: string, valid: boolean},
  contact: {value: string, valid: boolean, object: any},
  setName: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCode: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setStockEnabled: Dispatch<SetStateAction<{value: boolean, valid: boolean}>>,
  setStorageCenter: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setTreasuryCashAccounts: Dispatch<SetStateAction<{value: string[], valid: boolean}>>,
  setTreasuryCard: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setDefaultCashCurrency: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setContact: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>
};

const AppPosCashierForm: React.FunctionComponent<AppPosCashierFormProps> = ({component, name, code, stockEnabled, storageCenter, treasuryCashAccounts, treasuryCard, defaultCashCurrency, contact, setName, setCode, setStockEnabled, setStorageCenter, setTreasuryCashAccounts, setTreasuryCard, setDefaultCashCurrency, setContact}) => {
  const {companyForUser} = UseOutletContextPos()
  const {lang} = StateLanguage()

  const [localTreasuryAccounts, setLocalTreasuryAccounts] = useState<TreasuryAccount[]>([])
  const [localContacts, setLocalContacts] = useState<Contact[]>([])
  const [localStorageCenters, setLocalStorageCenters] = useState<StorageCenter[]>([])
  const [localDataLoaded, setLocalDataLoaded] = useState(false)
  const [search, setSearch] = useState<{text: string, list: Contact[]}>({text: '', list: []})

  const getAccountById = (id: string): TreasuryAccount | undefined =>
    localTreasuryAccounts.find(a => a.id === id)

  const isCurrencyConflict = (account: TreasuryAccount, selectedIds: string[]): boolean => {
    const selectedCurrencies = selectedIds
      .map(id => localTreasuryAccounts.find(a => a.id === id)?.currency)
      .filter(Boolean)
    return selectedCurrencies.includes(account.currency)
  }

  const hasCHFCashAccount = (selectedIds: string[]): boolean => {
    return selectedIds.some(id =>
      localTreasuryAccounts.find(a => a.id === id)?.currency === 'CHF'
    )
  }

  const getSelectedCurrencies = (): string[] => {
    return treasuryCashAccounts.value
      .map(id => getAccountById(id)?.currency)
      .filter(Boolean) as string[]
  }

  const toggleCashAccount = (id: string) => {
    const isSelected = treasuryCashAccounts.value.includes(id)
    const account = getAccountById(id)
    if (!account) return

    if (isSelected) {
      const newValue = treasuryCashAccounts.value.filter(x => x !== id)
      const isValid = newValue.length > 0 && hasCHFCashAccount(newValue)
      setTreasuryCashAccounts({ value: newValue, valid: isValid })

      // Update default currency if the removed account's currency was the default
      const remainingCurrencies = newValue.map(vid => getAccountById(vid)?.currency).filter(Boolean) as string[]
      if (!remainingCurrencies.includes(defaultCashCurrency.value) && remainingCurrencies.length > 0) {
        setDefaultCashCurrency({ value: remainingCurrencies[0], valid: true })
      }

      if (isValid) {
        legendValid(`${prefixLegend(component)}legend-treasury-cash-accounts-required`)
      } else {
        legendInvalid(`${prefixLegend(component)}legend-treasury-cash-accounts-required`)
      }
    } else {
      if (isCurrencyConflict(account, treasuryCashAccounts.value)) {
        return
      }
      const newValue = [...treasuryCashAccounts.value, id]
      const isValid = hasCHFCashAccount(newValue)
      setTreasuryCashAccounts({ value: newValue, valid: isValid })

      if (isValid) {
        legendValid(`${prefixLegend(component)}legend-treasury-cash-accounts-required`)
      }
    }
  }

  const handleChangeSearch = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let text: string = event.target.value
    let list: Contact[] = []

    if (text.length > 0) {
      for (let contactItem of localContacts) {
        let string: string = (contactItem.type === 'person') ? (`${contactItem.data.first_name} ${contactItem.data.last_name}`).toLowerCase() : contactItem.data.name.toLowerCase()
        if (string.indexOf(text.toLowerCase()) !== -1) {
          list.push(contactItem)
        }
      }
    } else {
      list = localContacts
    }

    setSearch({text: text, list: list})
  }

  const handleChangeName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setName({...name, value: event.target.value})
  }

  const handleChangeCode = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setCode({...code, value: event.target.value})
  }

  const handleChangeStorageCenter = (item: StorageCenter) => {
    setStorageCenter({value: item.id, valid: true, object: item})
    legendValid(`${prefixLegend(component)}legend-storage-center-required`)
  }

  const handleChangeContact = (item: Contact) => {
    setContact({value: item.id, valid: true, object: item})
    legendValid(`${prefixLegend(component)}legend-contact-required`)
  }

  const validateName = () => {
    evaluateLegendRequiredIcon(expressions.name, name, setName, `${prefixLegend(component)}input-name`, `${prefixLegend(component)}legend-name-valid`, `${prefixLegend(component)}legend-name-required`)
  }

  const validateCode = () => {
    evaluateLegendRequiredIcon(expressions.code, code, setCode, `${prefixLegend(component)}input-code`, `${prefixLegend(component)}legend-code-valid`, `${prefixLegend(component)}legend-code-required`)
  }

  function uploadDropdown() {
    setSearch({text: '', list: localContacts})
  }

  useEffect( () => {
    if (companyForUser) {
      Promise.all([
        TreasuryAccountDA.getTreasuryAccounts(companyForUser.id),
        StorageCenterDA.getStorageCenters(companyForUser.id, '', 'name', 'asc'),
        ApiDigitalAdminContact.getContacts(companyForUser.id, '', '', '', 'name', 1, 1000)
      ]).then(([treasuryRes, storageRes, contactsRes]) => {
        if (treasuryRes.status === 200) {
          const data = treasuryRes.data?.data ?? treasuryRes.data
          setLocalTreasuryAccounts(data.items ?? data.treasury_accounts ?? data ?? [])
        }
        if (storageRes.status === 200) {
          const data = storageRes.data?.data ?? storageRes.data
          setLocalStorageCenters(data.items ?? data.storage_centers ?? data ?? [])
        }
        if (contactsRes.status === 200) {
          const data = contactsRes.data?.data ?? contactsRes.data
          setLocalContacts(data.items ?? data.contacts ?? data ?? [])
        }
        setLocalDataLoaded(true)
      }).catch(() => {})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    uploadTooltip()

    if (localDataLoaded) {
      if (storageCenter.value && !storageCenter.object) {
        const found = localStorageCenters.find(s => s.id === storageCenter.value)
        if (found) setStorageCenter(prev => ({...prev, object: found}))
      }
      if (treasuryCard.value && !treasuryCard.object) {
        const found = localTreasuryAccounts.find(a => a.id === treasuryCard.value)
        if (found) setTreasuryCard(prev => ({...prev, object: found}))
      }
      if (contact.value && !contact.object) {
        const found = localContacts.find(c => c.id === contact.value)
        if (found) setContact(prev => ({...prev, object: found}))
      }
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localDataLoaded, localTreasuryAccounts, localContacts, localStorageCenters])

  const cashAccounts = localTreasuryAccounts.filter(item => item.type === 'cash')
  const bankAccountsCHF = localTreasuryAccounts.filter(item => item.type === 'bank' && item.currency === 'CHF')
  const selectedCurrencies = getSelectedCurrencies()

  return (
    <>
      { localDataLoaded &&
        <div className="form">
          <div className="row mb-3">
            <label className="col-lg-3 col-form-label required">{lang.labels.name}</label>
            <div className="col-lg-9">
              <input id={`${prefixLegend(component)}input-name`} className="form-control" type="text" autoComplete="off" name="name" value={name.value} onChange={handleChangeName} onKeyUp={validateName} onBlur={validateName} />
              <AppLegend component={component} attribute={{check: name.valid, name: "name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
            </div>
          </div>
          <div className="row mb-3">
            <label className="col-lg-3 col-form-label required">{lang.labels.code}</label>
            <div className="col-lg-9">
              <input id={`${prefixLegend(component)}input-code`} className="form-control" type="text" autoComplete="off" name="code" value={code.value} onChange={handleChangeCode} onKeyUp={validateCode} onBlur={validateCode} />
              <AppLegend component={component} attribute={{check: code.valid, name: "code", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
            </div>
          </div>
          <div className="row mb-3">
            <label className="col-lg-3 col-form-label">{lang.labels.manageStock}</label>
            <div className="col-lg-9">
              <div className="form-check form-switch form-check-custom form-check-solid mt-2">
                <input
                  type="checkbox"
                  className="form-check-input h-25px w-45px"
                  id={`${prefixLegend(component)}stock-enabled-toggle`}
                  checked={stockEnabled.value}
                  onChange={(e) => {
                    setStockEnabled({ value: e.target.checked, valid: true })
                    if (!e.target.checked) {
                      setStorageCenter({ value: '', valid: false, object: null })
                    }
                  }}
                />
                <label className="form-check-label ms-3" htmlFor={`${prefixLegend(component)}stock-enabled-toggle`}>
                  <span className="fw-bold">{lang.labels.manageStock}</span>
                </label>
              </div>
              <div className="form-text fs-8 mt-1">
                {stockEnabled.value ? lang.labels.stockManagedDescription : lang.labels.stockDisabledDescription}
              </div>
            </div>
          </div>
          { stockEnabled.value &&
          <div className="row mb-3">
            <label className="col-lg-3 col-form-label required">{lang.labels.storageCenter}</label>
            <div className="col-lg-9">
              <Dropdown>
                <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                  <span className="selection">
                    <span className="select2-selection select2-selection--single form-select">
                      <span className="select2-selection__rendered" role="textbox">
                        {storageCenter.valid && storageCenter.object ? storageCenter.object.name : (storageCenter.valid && storageCenter.value ? (localStorageCenters.find(s => s.id === storageCenter.value)?.name ?? lang.labels.selectOption) : lang.labels.selectOption)}
                      </span>
                    </span>
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100" >
                  <span className="select2-dropdown select2-dropdown--below">
                    <span className="select2-results">
                      <ul className="select2-results__options" role="listbox">
                        <li className={`select2-results__option select2-results__option--selectable ${localStorageCenters.length > 0 && "d-none"}`}>
                          <Dropdown.Item bsPrefix="select2-results__option__text">{lang.labels.selectOption}</Dropdown.Item>
                        </li>
                        { localStorageCenters.map (( (item, index) => { return (
                          <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === storageCenter.value && "select2-results__option--selected"}`} role="option" aria-selected={item.id === storageCenter.value}>
                            <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeStorageCenter(item)}>{item.name}</Dropdown.Item>
                          </li>
                        )}))}
                      </ul>
                    </span>
                  </span>
                </Dropdown.Menu>
              </Dropdown>
              <AppLegend component={component} attribute={{check: storageCenter.valid, name: 'storage-center', index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
            </div>
          </div>
          }
          <div className="row mb-3">
            <label className="col-lg-3 col-form-label required">{lang.labels.treasuryCashAccount}</label>
            <div className="col-lg-9">
              { cashAccounts.length > 0
                ?
                <div className="border rounded p-3">
                  <div className="text-muted fs-8 mb-2">{lang.labels.selectOneAccountPerCurrency ?? 'Select one account per currency. At least one CHF account is required.'}</div>
                  { cashAccounts.map((account) => {
                    const isSelected = treasuryCashAccounts.value.includes(account.id)
                    const conflict = !isSelected && isCurrencyConflict(account, treasuryCashAccounts.value)
                    return (
                      <label
                        key={account.id}
                        className={`d-flex justify-content-between align-items-center p-2 rounded mb-1 cursor-pointer ${isSelected ? 'bg-light-primary' : conflict ? 'bg-light text-muted' : 'bg-light'}`}
                      >
                        <div className="d-flex align-items-center">
                          <input
                            type="checkbox"
                            className="form-check-input me-3"
                            checked={isSelected}
                            disabled={conflict}
                            onChange={() => toggleCashAccount(account.id)}
                          />
                          <span className={`fw-bold ${conflict ? 'text-muted' : ''}`}>{account.name}</span>
                        </div>
                        <span className={`badge ${isSelected ? 'badge-primary' : 'badge-light'}`}>
                          {account.currency}
                        </span>
                      </label>
                    )
                  })}
                  { treasuryCashAccounts.value.length > 0 && !hasCHFCashAccount(treasuryCashAccounts.value) &&
                    <div className="text-danger fs-8 mt-2">{lang.labels.atLeastOneCHFCashAccountRequired ?? 'At least one CHF cash account is required.'}</div>
                  }
                </div>
                :
                <div className="text-muted">{lang.labels.noRecordsFound}</div>
              }
              <div id={`${prefixLegend(component)}legend-treasury-cash-accounts-required`} className={`fv-plugins-message-container invalid-feedback ${treasuryCashAccounts.valid ? 'd-none' : ''}`}>
                <div data-field="treasury-cash-accounts" data-validator="notEmpty">{lang.labels.required}</div>
              </div>
            </div>
          </div>
          <div className="row mb-3">
            <label className="col-lg-3 col-form-label required">{lang.labels.treasuryCardAccount}</label>
            <div className="col-lg-9">
              <AppInputTreasuryAccount component={component} type={"card"} solid={true} treasuryAccountsForCompany={bankAccountsCHF} treasuryAccount={treasuryCard} setTreasuryAccount={setTreasuryCard} />
            </div>
          </div>
          { selectedCurrencies.length >= 2 &&
            <div className="row mb-3">
              <label className="col-lg-3 col-form-label">{lang.labels.defaultCurrency ?? 'Default currency'}</label>
              <div className="col-lg-9">
                <select
                  className="form-select"
                  value={defaultCashCurrency.value}
                  onChange={(e) => setDefaultCashCurrency({ value: e.target.value, valid: true })}
                >
                  { selectedCurrencies.map(currCode => (
                    <option key={currCode} value={currCode}>{currCode}</option>
                  ))}
                </select>
              </div>
            </div>
          }
          <div className="row">
            <label className="col-lg-3 col-form-label d-flex align-items-center">
              <span className="required">{lang.labels.customer}</span>
              <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.defaultCustomerSale}><FaExclamationCircle /></i>
            </label>
            <div className="col-lg-9">
              <Dropdown onToggle={uploadDropdown}>
                <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                  <span className="selection">
                    <span className="select2-selection select2-selection--single form-select">
                      <span className="select2-selection__rendered" role="textbox">
                        {contact.valid && contact.object ? formatContact(contact.object) : (contact.valid && contact.value ? (localContacts.find(c => c.id === contact.value) ? formatContact(localContacts.find(c => c.id === contact.value)) : lang.labels.selectOption) : lang.labels.selectOption)}
                      </span>
                    </span>
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                  <span className="select2-dropdown select2-dropdown--below">
                    <span className="select2-search select2-search--dropdown">
                      <input className="select2-search__field" type="text" autoComplete="off" name="contact" value={search.text} onChange={handleChangeSearch} />
                    </span>
                    <span className="select2-results">
                      <ul className="select2-results__options" role="listbox">
                        { search.list.length > 0
                          ?
                          <>
                            { search.list.map (( (item, index) => { return (
                              <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === contact.value && "select2-results__option--selected"}`} role="option" aria-selected={item.id === contact.value}>
                                <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeContact(item)}>{formatContact(item)}</Dropdown.Item>
                              </li>
                            )}))}
                          </>
                          :
                          <li className="select2-results__option select2-results__message" role="alert">{lang.labels.noResultsFound}</li>
                        }
                      </ul>
                    </span>
                  </span>
                </Dropdown.Menu>
              </Dropdown>
              <AppLegend attribute={{check: contact.valid, name: "contact", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
            </div>
          </div>
        </div>
      }
    </>
  )
};

export default AppPosCashierForm;
